import React, { useRef } from "react";
import { ChevronLeft, ChevronRight, ShieldPlus, Sparkles } from "lucide-react";
import { guardCards } from "@/data/guardCards";

const SHOPIFY_URL =
  "https://invisibleplayerbook.myshopify.com/products/the-invisible-player-how-reading-bodies-and-minds-gave-me-an-unfair-advantage";
const CUSTOM_GUARD_URL = "https://glistening-kataifi-292e57.netlify.app";

export default function GuardCardsCarousel() {
  const railRef = useRef(null);
  const drag = useRef({ active: false, startX: 0, scroll: 0, moved: false });

  const scrollBy = (dir) => {
    const rail = railRef.current;
    if (rail) rail.scrollBy({ left: dir * 300, behavior: "smooth" });
  };

  const onWheel = (e) => {
    const rail = railRef.current;
    if (!rail) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      rail.scrollLeft += e.deltaY;
    }
  };

  const onPointerDown = (e) => {
    const rail = railRef.current;
    drag.current = { active: true, startX: e.clientX, scroll: rail.scrollLeft, moved: false };
    rail.classList.add("dragging");
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const rail = railRef.current;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    rail.scrollLeft = drag.current.scroll - dx;
  };
  const endDrag = () => {
    drag.current.active = false;
    railRef.current?.classList.remove("dragging");
  };

  return (
    <div className="relative" data-testid="guard-carousel">
      <div className="flex items-center justify-between mb-5">
        <span className="eyebrow">Swipe · drag · scroll</span>
        <div className="flex gap-3">
          <button
            aria-label="Previous"
            onClick={() => scrollBy(-1)}
            data-testid="carousel-prev"
            className="w-11 h-11 rounded-full border border-gold/40 text-gold flex items-center justify-center hover:bg-gold/10 transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            aria-label="Next"
            onClick={() => scrollBy(1)}
            data-testid="carousel-next"
            className="w-11 h-11 rounded-full border border-gold/40 text-gold flex items-center justify-center hover:bg-gold/10 transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      <div
        ref={railRef}
        className="rail"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
      >
        {guardCards.map((c) => (
          <article key={c.id} className="guard-card" data-testid={`guard-card-${c.id}`}>
            <div className="guard-thumb">
              {c.img ? (
                <img
                  src={c.img}
                  alt={c.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  draggable="false"
                  loading="lazy"
                />
              ) : (
                <>
                  <ShieldPlus size={64} className="text-gold/35" strokeWidth={1} />
                  <span className="absolute bottom-3 right-4 text-[10px] tracking-widest text-gold/30 uppercase">
                    image placeholder
                  </span>
                </>
              )}
            </div>
            <div className="p-5 flex flex-col min-h-[190px]">
              <span className="eyebrow !text-[0.6rem] !tracking-[0.28em]">{c.archetype}</span>
              <h4 className="font-display text-base leading-tight text-[#f3ecd9] font-bold mt-1">{c.title}</h4>
              <p className="text-sm text-[#9c968a] mt-1 italic">{c.meme}</p>
              <div className="mt-auto flex items-center justify-between pt-3">
                <span className="font-display text-gold text-lg">{c.price}</span>
                <a
                  href={SHOPIFY_URL}
                  target="_top"
                  rel="noopener"
                  data-testid={`claim-btn-${c.id}`}
                  className="btn-gold !text-[0.62rem] !px-4 !py-2"
                >
                  CLAIM YOURS
                </a>
              </div>
            </div>
          </article>
        ))}

        {/* SPECIAL — bespoke card */}
        <article className="guard-card guard-special" data-testid="guard-card-special">
          <div className="guard-thumb">
            <Sparkles size={64} className="text-gold" strokeWidth={1.2} />
          </div>
          <div className="p-5">
            <h4 className="font-display text-lg gold-gradient leading-tight">
              WANT ONE THAT EXISTS ONLY FOR YOU?
            </h4>
            <p className="text-[13px] text-[#b8b1a3] mt-2 leading-relaxed">
              Describe your idea. Our designer turns it into a one-of-a-kind guardian —
              sculpted, printed and finished for you alone. You approve before we print.
            </p>
            <a
              href={CUSTOM_GUARD_URL}
              target="_top"
              rel="noopener"
              data-testid="create-guard-btn"
              className="btn-gold mt-4 !text-[0.72rem] !px-5 !py-3"
            >
              CREATE MY GUARD — $49.90
            </a>
          </div>
        </article>
      </div>
    </div>
  );
}
